/**
 * Social sign-in — Google, Apple, Microsoft, LinkedIn — for both web portals.
 *
 * One round trip, one set of paths, whatever the provider:
 *
 *   /login or /signup  → provider  → SSO_CALLBACK_PATH (Clerk finishes it)
 *        ↑ an error: Clerk returns here and leaves it on the attempt
 *                                   → SIGN_UP_CONTINUE_PATH when the new
 *                                     account still needs something (an
 *                                     email Clerk must verify — Microsoft's,
 *                                     typically)
 *                                   → SIGN_IN_CONTINUE_PATH when a known
 *                                     account must still prove itself with a
 *                                     code (a provider that does not vouch
 *                                     for the email; a new device)
 *                                   → the page the person was going to
 *
 * Every one of those is our own page: Clerk's callback builds any target it is
 * not given from the instance's hosted Account Portal.
 *
 * The buttons are the providers the Clerk instance has switched on, in our
 * order (`useEnabledSocialProviders`); `npm run qa:clerk-social-providers`
 * checks each tier's instance offers all of `SOCIAL_PROVIDERS`.
 *
 * A session the browser still holds makes Clerk refuse the start with "signed
 * in"; it is signed out and the start runs once more (`retryAfterStaleSession`).
 *
 * Nothing here throws at the caller or renders words: failures are Clerk's
 * errors, for `useAuthErrorMessage` to put into the reader's language.
 */

import { useAuth, useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';
import type { EnvironmentResource } from '@clerk/shared/types';
import {
  SOCIAL_PROVIDER_STRATEGY,
  SOCIAL_PROVIDERS,
  socialProviderOf,
  type SocialProvider,
} from '@tonnie/foundation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { retryAfterStaleSession } from './stale-session';

/** Where every provider sends the person back; `AuthenticateWithRedirectCallback` lives here. */
export const SSO_CALLBACK_PATH = '/auth/sso-callback';
/** Where Clerk sends a social sign-up that still needs something. */
export const SIGN_UP_CONTINUE_PATH = '/signup/continue';
/**
 * The marker on /login that says "a social sign-in stopped for a code — finish
 * it here" (`useSignInContinuation`). A query, not a route: /login is the page
 * that already asks for sign-in codes, and only Clerk's callback adds it, so a
 * plain /login never picks up an attempt the person walked away from.
 */
export const SIGN_IN_CONTINUE_PARAM = 'continue';
export const SIGN_IN_CONTINUE_VALUE = 'sso';
/** Where Clerk sends a social sign-in that needs a code (`firstFactorUrl` / `secondFactorUrl`). */
export const SIGN_IN_CONTINUE_PATH = `/login?${SIGN_IN_CONTINUE_PARAM}=${SIGN_IN_CONTINUE_VALUE}`;

/**
 * Where the person was going, kept across the provider round trip for
 * SIGN_UP_CONTINUE_PATH — Clerk hands `redirectUrlComplete` to its callback
 * but not to the continue page, and a business sign-up is bound for
 * /onboarding or an invitation, not the fallback. Session-scoped; storage
 * that is unavailable (a private window) only loses the destination.
 */
const COMPLETE_URL_KEY = 'tonnie.social.completeUrl';

function keepCompleteUrl(url: string): void {
  try {
    sessionStorage.setItem(COMPLETE_URL_KEY, url);
  } catch {
    // Storage blocked — the continue page falls back to the caller's default.
  }
}

function takeCompleteUrl(): string | null {
  try {
    return sessionStorage.getItem(COMPLETE_URL_KEY);
  } catch {
    return null;
  }
}

function forgetCompleteUrl(): void {
  try {
    sessionStorage.removeItem(COMPLETE_URL_KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/**
 * What the providers are offered for: signing in or up (`signIn` — the buttons
 * on /login and /signup, web and phone), or connecting one to the signed-in
 * account (`connect` — Settings → Security).
 */
export type SocialProviderUse = 'signIn' | 'connect';

export interface EnabledSocialProviders {
  /** In `SOCIAL_PROVIDERS` order; empty until `ready`. */
  providers: readonly SocialProvider[];
  /** Clerk has loaded and its instance settings were read. */
  ready: boolean;
}

const NOT_READY: EnabledSocialProviders = { providers: [], ready: false };

/**
 * The Clerk instance's own settings. clerk-js keeps them on the instance as
 * `__unstable__environment` — the getter clerk-react's `useClerk()` forwards
 * and clerk-expo reads itself to cache them — but Clerk's public `LoadedClerk`
 * type does not declare it. This is the one place the platform reads it.
 */
interface ClerkWithEnvironment {
  readonly __unstable__environment: EnvironmentResource | null | undefined;
}

function hasEnvironment(clerk: object): clerk is ClerkWithEnvironment {
  return '__unstable__environment' in clerk;
}

/**
 * The social providers the Clerk instance has switched on, in our order.
 *
 * `SOCIAL_PROVIDERS` is the platform's list and order; the instance decides
 * which of them work. Clerk's lists are filtered by membership, never used as
 * they come — theirs are alphabetical:
 *   - `signIn`  → `userSettings.authenticatableSocialStrategies` (a provider
 *                 enabled AND allowed to sign in);
 *   - `connect` → `userSettings.socialProviderStrategies` (every enabled one —
 *                 an account may connect a provider it does not sign in with).
 *
 * Until Clerk has loaded there are no providers and `ready` is false — the
 * buttons were disabled until then anyway, so none appears only to vanish.
 * The settings are read once loaded; they do not change while the page lives.
 *
 * A loaded Clerk without the environment getter is a Clerk API change, not a
 * state to render around: it throws, loudly, rather than show every button
 * (the failure this hook exists to end) or none (sign-in silently gone). A
 * test that renders a screen over a mocked Clerk mocks THIS hook from
 * `@tonnie/sdk` with the providers it wants — the seam is the hook, never a
 * production fallback.
 */
export function useEnabledSocialProviders(use: SocialProviderUse): EnabledSocialProviders {
  const clerk = useClerk();
  const { isLoaded } = useAuth();

  if (isLoaded && !hasEnvironment(clerk)) {
    throw new Error(
      'Clerk is loaded but exposes no environment (__unstable__environment): the social providers it offers cannot be read'
    );
  }
  const environment = isLoaded && hasEnvironment(clerk) ? clerk.__unstable__environment : null;

  return useMemo(() => {
    if (!environment) return NOT_READY;
    const strategies: readonly string[] =
      use === 'signIn'
        ? environment.userSettings.authenticatableSocialStrategies
        : environment.userSettings.socialProviderStrategies;
    return {
      providers: SOCIAL_PROVIDERS.filter((provider) =>
        strategies.includes(SOCIAL_PROVIDER_STRATEGY[provider])
      ),
      ready: true,
    };
  }, [environment, use]);
}

export type SocialStartOutcome = { outcome: 'redirecting' } | { outcome: 'failed'; error: unknown };

/**
 * Start a social sign-in (`sign-in` page) or sign-up (`sign-up` page). Either
 * way Clerk moves between the two by itself on the way back — an unknown
 * account becomes a sign-up, a known one a sign-in.
 */
export function useSocialSignIn(mode: 'sign-in' | 'sign-up') {
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const clerk = useClerk();
  const ready = mode === 'sign-in' ? signInLoaded && !!signIn : signUpLoaded && !!signUp;

  const start = useCallback(
    async (provider: SocialProvider, completeUrl: string): Promise<SocialStartOutcome> => {
      const params = {
        strategy: SOCIAL_PROVIDER_STRATEGY[provider],
        redirectUrl: SSO_CALLBACK_PATH,
        redirectUrlComplete: completeUrl,
      };
      keepCompleteUrl(completeUrl);
      try {
        if (mode === 'sign-in') {
          if (!signIn) throw new Error('Clerk sign-in is not loaded');
          await retryAfterStaleSession(
            () => signIn.authenticateWithRedirect(params),
            () => clerk.signOut()
          );
        } else {
          if (!signUp) throw new Error('Clerk sign-up is not loaded');
          await retryAfterStaleSession(
            () => signUp.authenticateWithRedirect(params),
            () => clerk.signOut()
          );
        }
        return { outcome: 'redirecting' };
      } catch (error) {
        return { outcome: 'failed', error };
      }
    },
    [mode, signIn, signUp, clerk]
  );

  return { ready, start };
}

/**
 * The error a social sign-in came back with, if any. When the provider round
 * trip fails, Clerk sends the person back to the sign-in / sign-up page and
 * leaves the reason on the attempt — `signIn.firstFactorVerification.error`
 * or `signUp.verifications.externalAccount.error`. Without reading it, the
 * page simply reappears and the person never learns why.
 *
 * A sign-in waiting for a code is not a failure: Clerk sent it to
 * SIGN_IN_CONTINUE_PATH to finish, and what the provider round trip left on
 * the attempt (a sign-up's `external_account_exists` before the transfer, say)
 * is how it got there, not something to tell the person.
 */
export function useSocialRedirectError(): {
  error: unknown;
  provider: SocialProvider | null;
} | null {
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();

  if (signIn?.status === 'needs_first_factor' || signIn?.status === 'needs_second_factor') {
    return null;
  }

  const fromSignIn = signIn?.firstFactorVerification;
  if (fromSignIn?.error && socialProviderOf(fromSignIn.strategy)) {
    return { error: fromSignIn.error, provider: socialProviderOf(fromSignIn.strategy) };
  }
  const fromSignUp = signUp?.verifications?.externalAccount;
  if (fromSignUp?.error && socialProviderOf(fromSignUp.strategy)) {
    return { error: fromSignUp.error, provider: socialProviderOf(fromSignUp.strategy) };
  }
  return null;
}

/**
 * A social sign-in Clerk stopped for a code, finished on the sign-in page
 * (SIGN_IN_CONTINUE_PATH): whether this page was opened for one — `marker` is
 * the page's `SIGN_IN_CONTINUE_PARAM` value — and, on the web, where the
 * person was going when they chose the provider (Clerk does not hand
 * `redirectUrlComplete` on to the page it sends them to). The screen resumes
 * the attempt with `useEmailFirstSignIn().resume()` and calls `finished` once
 * signed in.
 */
export function useSignInContinuation(marker: string | null | undefined) {
  const [continuation] = useState(() => {
    const resuming = marker === SIGN_IN_CONTINUE_VALUE;
    return {
      resuming,
      completeUrl: resuming ? takeCompleteUrl() : null,
      finished: forgetCompleteUrl,
    };
  });
  return continuation;
}

/** Where a social sign-up stands when Clerk sends it to SIGN_UP_CONTINUE_PATH. */
export type SignUpContinuation =
  | { step: 'loading' }
  /** No sign-up in progress — the page was opened directly, or it is finished. */
  | { step: 'none' }
  /** The provider's email must be proven: a code went to it. */
  | { step: 'verify_email'; email: string }
  /** Clerk asks for something this product does not collect. Named, for the report. */
  | { step: 'unsupported'; missing: string[] }
  | { step: 'complete' };

export type ContinuationOutcome = { outcome: 'done' } | { outcome: 'failed'; error: unknown };

/**
 * Finish a social sign-up Clerk could not complete on its own. The one gap
 * this instance can leave is an unverified email (Microsoft does not vouch for
 * its addresses; Clerk asks for proof), so a code is sent to it once and the
 * person types it here. Anything else is reported as `unsupported`.
 */
export function useSignUpContinuation() {
  const { signUp, isLoaded, setActive } = useSignUp();
  const [codeSendError, setCodeSendError] = useState<unknown>(null);
  const [completeUrl] = useState(takeCompleteUrl);
  const codeSent = useRef(false);

  const status = signUp?.status ?? null;
  const unverified = signUp?.unverifiedFields ?? [];
  const missing = signUp?.missingFields ?? [];
  const email = signUp?.emailAddress ?? null;

  const state: SignUpContinuation = !isLoaded
    ? { step: 'loading' }
    : !signUp || status === null || status === 'abandoned'
      ? { step: 'none' }
      : status === 'complete'
        ? { step: 'complete' }
        : missing.length === 0 &&
            unverified.length === 1 &&
            unverified[0] === 'email_address' &&
            email
          ? { step: 'verify_email', email }
          : { step: 'unsupported', missing: [...missing, ...unverified] };

  const sendCode = useCallback(async (): Promise<ContinuationOutcome> => {
    if (!signUp) return { outcome: 'failed', error: new Error('No sign-up in progress') };
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setCodeSendError(null);
      return { outcome: 'done' };
    } catch (error) {
      setCodeSendError(error);
      return { outcome: 'failed', error };
    }
  }, [signUp]);

  // The first code goes out on arrival — once.
  useEffect(() => {
    if (state.step === 'verify_email' && !codeSent.current) {
      codeSent.current = true;
      void sendCode();
    }
  }, [state.step, sendCode]);

  const verify = useCallback(
    async (code: string): Promise<ContinuationOutcome> => {
      if (!signUp || !setActive) {
        return { outcome: 'failed', error: new Error('No sign-up in progress') };
      }
      try {
        const result = await signUp.attemptEmailAddressVerification({ code });
        if (result.status !== 'complete' || !result.createdSessionId) {
          return {
            outcome: 'failed',
            error: new Error(`Sign-up still ${result.status} after the email code`),
          };
        }
        await setActive({ session: result.createdSessionId });
        forgetCompleteUrl();
        return { outcome: 'done' };
      } catch (error) {
        return { outcome: 'failed', error };
      }
    },
    [signUp, setActive]
  );

  /** `completeUrl`: where the person was going when they chose the provider, if known. */
  return { state, completeUrl, codeSendError, sendCode, verify };
}
